import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import {
  BaseActivity,
  BaseActivityArticlesNames,
  BaseActivityDetailedContent,
  BaseActivityText,
} from '@/components/activities/BaseActivity/BaseActivity';
import IconUserRefusal from '@/icons/UserRefusal.svg';
import IconUserValidation from '@/icons/UserValidation.svg';
import { ARTICLE_CANCELLATION_REASONS, STORE_ARTICLE_CANCELLATION_REASONS } from '@/models/article';

export const ArticleValidationChoiceActivity = ({
  activities,
  displayArticleName,
}: {
  activities: ActivityOfType<'article_validation_choice'>[];
  displayArticleName: boolean;
}) => {
  const { i18n } = useLingui();

  const firstActivity = activities[0];
  const creatorName = firstActivity.creator?.name;
  const articleName = (
    <BaseActivityArticlesNames activities={activities} displayArticleName={displayArticleName} />
  );

  // Accepted activies are grouped
  if (firstActivity.data.value === 'accepted') {
    return (
      <BaseActivity
        createdAt={firstActivity.createdAt}
        type="primary"
        icon={<IconUserValidation />}
        message={
          <BaseActivityText>
            {creatorName ? (
              activities.length > 1 ? (
                <Trans id="activities.article-validation-choice.accepted.message.client.on-behalf.plural">
                  <strong>{creatorName}</strong> accepted the quote for the items {articleName} on
                  behalf of the client
                </Trans>
              ) : (
                <Trans id="activities.article-validation-choice.accepted.message.client.on-behalf">
                  <strong>{creatorName}</strong> accepted the quote for the item {articleName} on
                  behalf of the client
                </Trans>
              )
            ) : activities.length > 1 ? (
              <Trans id="activities.article-validation-choice.accepted.message.client.plural">
                <strong>The client</strong> accepted the quote for the items {articleName}
              </Trans>
            ) : (
              <Trans id="activities.article-validation-choice.accepted.message.client">
                <strong>The client</strong> accepted the quote for the item {articleName}
              </Trans>
            )}
          </BaseActivityText>
        }
      />
    );
  }

  // Refused activities are not grouped
  if (firstActivity.data.value === 'refused') {
    const { reason, otherReason } = firstActivity.data;

    const reasonLabel =
      reason === 'other' || reason === 'store-other' ? (
        <Trans id="activities.article-validation-choice.refused.message.reason-other">
          Other - {otherReason}
        </Trans>
      ) : (
        i18n._(ARTICLE_CANCELLATION_REASONS.find(({ id }) => id === reason)!.label)
      );

    const isRefusedByTheStore =
      !!reason && STORE_ARTICLE_CANCELLATION_REASONS.some(({ id }) => id === reason);

    return (
      <BaseActivity
        createdAt={firstActivity.createdAt}
        type="danger"
        icon={<IconUserRefusal />}
        message={
          <BaseActivityText>
            {isRefusedByTheStore ? (
              <Trans id="activities.article-validation-choice.refused.message.store">
                <strong>The store</strong> refused the quote for the item {articleName}
              </Trans>
            ) : creatorName ? (
              <Trans id="activities.article-validation-choice.refused.message.client.on-behalf">
                <strong>{creatorName}</strong> refused the quote for the item {articleName} on
                behalf of the client
              </Trans>
            ) : (
              <Trans id="activities.article-validation-choice.refused.message.client">
                <strong>The client</strong> refused the quote for the item {articleName}
              </Trans>
            )}
          </BaseActivityText>
        }
      >
        {!!reasonLabel && <BaseActivityDetailedContent>{reasonLabel}</BaseActivityDetailedContent>}
      </BaseActivity>
    );
  }

  return null;
};
